import { useState } from "react";
import { useAlert } from "src/contexts/AlertContext";
import { ClusterService } from "src/services/clusters";
import { DomainService } from "src/services/domains";
import { DomainType } from "src/types";

interface UseDomainsListProps {
  organizationId: string;
}

export const useDomainsList = ({ organizationId }: UseDomainsListProps) => {
  const [domains, setDomains] = useState<DomainType[]>([]);
  const [clusters, setClusters] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingForm, setIsLoadingForm] = useState(false);
  const [isLoadingClusters, setIsLoadingClusters] = useState(false);
  const [isLoadingDeletion, setIsLoadingDeletion] = useState(false);
  const [selectedDomains, setSelectedDomains] = useState<DomainType[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredData, setFilteredData] = useState<DomainType[]>([]); 

  const { showAlert } = useAlert();

  const fetchClusters = (async () => {
    try {
      setIsLoadingClusters(true);
      const [clustersData] = await Promise.all([
        ClusterService.getClustersByOrganization(organizationId)
      ]);
      setClusters(clustersData.records?.map((c: any) => ({ label: c?.name, value: c?.id })));
    } catch (error: any) {
      if (error.response?.status === 403) {
        showAlert(
          "Clusters list",
          error.response.data || "Get Clusters List Failed",
          "warning"
        );
      } else {
         showAlert(
           "Clusters list",
           error.response.data || "Get Clusters List Failed",
           "error"
         );
      }
    } finally {
      setIsLoadingClusters(false);
    }
  });

  const fetchDomains = (async () => {
    try {
      setIsLoading(true);
      const domainsData = await 
      DomainService.getDomains(organizationId);
      setDomains(domainsData);
      setFilteredData(domainsData)
    } catch (error: any) {
       if (error.response?.status === 403) {
         showAlert(
           "Domains list",
           error.response.data || "Get Domains List Failed",
           "error"
         );
       }
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  const createDomain = async (data: any) => {
    try {
      setIsLoadingForm(true);
      await DomainService.createDomain(organizationId, data);
      showAlert("Domain creation",'Domain Created Successfully', 'success');
      await fetchDomains();
      return true;
    } catch (error: any) {
      showAlert("Domain creation",error?.response?.data || 'Domain Creation Failed', 'error');
      return false;
    } finally {
      setIsLoadingForm(false);
    }
  };

  const updateDomain = async (data: any) => {
    try {
      setIsLoadingForm(true);
      await DomainService.updateDomain(organizationId, data.id, {
        ...data,
        organizationID: organizationId,
      });
      showAlert("Domain update",'Domain Updated Successfully', 'success');
      await fetchDomains();
      return true;
    } catch (error: any) {
      showAlert("Domain update",error?.response?.data || 'Domain Update Failed', 'error');
      return false;
    } finally {
      setIsLoadingForm(false);
    }
  };

  const deleteDomain = async (domainId: string) => {
    try {
      setIsLoadingDeletion(true);
      await DomainService.deleteDomain(organizationId, domainId);
      showAlert("Domain deletion",'Domain Deleted Successfully', 'success');
      await fetchDomains();
      return true;
    } catch (error: any) {
      showAlert("Domain deletion",error?.response?.data || 'Domain Deletion Failed', 'error');
      return false;
    } finally {
      setIsLoadingDeletion(false);
    }
  };

  return {
    domains,
    isLoading,
    selectedDomains,
    clusters,
    isLoadingForm,
    isLoadingDeletion,
    isLoadingClusters,
    searchTerm,
    setSelectedDomains,
    fetchClusters,
    fetchDomains,
    createDomain,
    updateDomain,
    deleteDomain,
    setSearchTerm,
    filteredData,
    setFilteredData
  };
};
